/**
 * API клиент для notifications-service
 */
import { apiClient } from '@/api'
import {
  NOTIFICATION_APP,
  type ChannelPreference,
  type NotificationsListResponse,
  type TelegramBotApi,
  type TypePreference,
  type WebPushSubscriptionData,
} from '../types/notifications.types'

const BASE_PATH = '/api/v1/notifications'

export const notificationsApi = {
  // --- Настройки каналов ---

  async getPreferences(): Promise<ChannelPreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences`)
    return data.preferences ?? []
  },

  async updatePreferences(preferences: ChannelPreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences`, { preferences })
  },

  // --- Настройки по типам событий (DEV-701/DEV-767): нет записи = включён ---

  async getEventPreferences(): Promise<TypePreference[]> {
    const { data } = await apiClient.get(`${BASE_PATH}/preferences/events`)
    return data.preferences ?? []
  },

  async updateEventPreferences(preferences: TypePreference[]): Promise<void> {
    await apiClient.put(`${BASE_PATH}/preferences/events`, { preferences })
  },

  // --- Web Push ---

  async getVAPIDPublicKey(): Promise<string> {
    const { data } = await apiClient.get(`${BASE_PATH}/webpush/vapid-key`)
    return data.vapidPublicKey
  },

  async registerWebPush(subscription: WebPushSubscriptionData): Promise<string> {
    const { data } = await apiClient.post(`${BASE_PATH}/webpush/subscribe`, {
      endpoint: subscription.endpoint,
      p256dh_key: subscription.p256dhKey,
      auth_key: subscription.authKey,
      user_agent: subscription.userAgent,
    })
    return data.subscriptionId
  },

  async unregisterWebPush(endpoint: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/webpush/unsubscribe`, { endpoint })
  },

  // --- Telegram-боты: общий и бот лидов (DEV-2320) — один контракт, разные пути ---

  telegramBot: telegramBotApi(`${BASE_PATH}/telegram`),
  telegramLeadsBot: telegramBotApi(`${BASE_PATH}/telegram-leads`),

  // --- История уведомлений ---

  async listNotifications(page = 1, pageSize = 20): Promise<NotificationsListResponse> {
    // DEV-302: пагинация — вложенный объект vinprop.common.v1.PageRequest/PageInfo (uint64 → строки)
    const { data } = await apiClient.get(BASE_PATH, {
      params: { 'pagination.page': page, 'pagination.perPage': pageSize, app: NOTIFICATION_APP },
    })
    return {
      notifications: (data.notifications ?? []).map(mapNotification),
      total: parseInt(data.pagination?.total ?? '0', 10) || 0,
      page: parseInt(data.pagination?.page ?? '0', 10) || page,
      pageSize: parseInt(data.pagination?.perPage ?? '0', 10) || pageSize,
    }
  },

  async markRead(notificationId: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/${notificationId}/read`)
  },

  // app обязателен и здесь, и в счётчике: иначе «прочитать всё» в CRM гасило бы
  // бейдж трекера, а счётчик считал бы чужие уведомления (DEV-1964).
  async markAllRead(): Promise<number> {
    const { data } = await apiClient.post(`${BASE_PATH}/read-all`, { app: NOTIFICATION_APP })
    return data.markedCount ?? 0
  },

  async getUnreadCount(): Promise<number> {
    const { data } = await apiClient.get(`${BASE_PATH}/unread-count`, {
      params: { app: NOTIFICATION_APP },
    })
    return data.count ?? 0
  },
}

/** Ручки привязки бота по базовому пути: link-code, status и DELETE на сам путь. */
function telegramBotApi(path: string): TelegramBotApi {
  return {
    async getLinkCode() {
      const { data } = await apiClient.get(`${path}/link-code`)
      return {
        linkCode: data.linkCode,
        botUsername: data.botUsername,
        deepLink: data.deepLink,
        expiresInSeconds: data.expiresInSeconds,
      }
    },

    async getStatus() {
      const { data } = await apiClient.get(`${path}/status`)
      return {
        linked: data.linked,
        telegramUsername: data.telegramUsername,
        verified: data.verified,
      }
    },

    async unlink() {
      await apiClient.delete(path)
    },
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapNotification(raw: any) {
  let metadata: Record<string, unknown> = {}
  if (raw.metadata) {
    try {
      metadata = typeof raw.metadata === 'string' ? JSON.parse(raw.metadata) : raw.metadata
    } catch {
      metadata = {}
    }
  }

  return {
    id: raw.id,
    notificationType: raw.notificationType ?? raw.notification_type,
    title: raw.title,
    body: raw.body,
    metadata,
    channelsSent: raw.channelsSent ?? raw.channels_sent ?? [],
    isRead: raw.isRead ?? raw.is_read ?? false,
    createdAt: raw.createdAt ?? raw.created_at,
    readAt: raw.readAt ?? raw.read_at ?? null,
    app: raw.app ?? NOTIFICATION_APP,
    url: raw.url ?? '',
  }
}
